<template>
  <component
    :is="tag"
    class="button"
    :type="type"
    :class="classes"
    :disabled="isDisabled"
    :href="href"
    @click="handleClick"
  >
    <span class="slot">
      <slot />
      <i v-if="icon" :class="'icon icon-' + icon" />
    </span>

    <span v-if="loading" class="loading"><i class="icon icon-spinner" /></span>
  </component>
</template>

<script>
export default {
  name: "CustomButtonV3",
  props: {
    tag: {
      type: String,
      default: "button",
      validator(value) {
        return ["a", "button"].includes(value);
      },
    },
    size: {
      type: String,
      default: "large",
      validator(value) {
        return ["small", "medium", "large"].includes(value);
      },
    },
    width: {
      type: String,
      default: "auto",
      validator(value) {
        return ["auto", "full"].includes(value);
      },
    },
    theme: {
      type: String,
      default: "primary",
      validator(value) {
        return [
          "primary",
          "primary-light",
          "secondary",
          "outline",
          "outline-light",
          "underline",
        ].includes(value);
      },
    },
    type: {
      type: String,
      default: "button",
      validator(value) {
        return ["button", "submit"].includes(value);
      },
    },
    href: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: null,
    },
  },
  emits: ["click"],
  computed: {
    classes() {
      const hasDefaultSlot =
        this.$slots.default && this.$slots.default().length > 0;

      return {
        "button--medium": this.size === "medium",
        "button--small": this.size === "small",
        "button--secondary": this.theme === "secondary",
        "button--outline": this.theme === "outline",
        "button--outline-light": this.theme === "outline-light",
        "button--primary-light": this.theme === "primary-light",
        "button--underline": this.theme === "underline",
        "button--disabled": this.disabled,
        "button--with-icon": this.icon,
        "button--icon-only": !hasDefaultSlot && this.icon,
        "button--full": this.width === "full",
        "button--loading": this.loading,
      };
    },
    isDisabled() {
      return this.disabled || this.loading;
    },
  },
  methods: {
    handleClick() {
      this.$emit("click");
    },
  },
};
</script>

<style lang="scss" scoped>
.button {
  padding: 15px 28px;
  background-color: $color-black;
  color: $color-white;
  border-radius: 4px;
  @include utility-button(13px);
  border: none;
  outline: none;
  text-decoration: none;
  text-align: center;

  &--small {
    padding: 6px 17px;
  }

  &--medium {
    padding: 11px 24px;
  }

  &--secondary {
    background-color: $color-grey-light;
    color: $color-black;
  }

  &--primary-light {
    background-color: $color-white;
    color: $color-black;
  }

  &--outline {
    background-color: transparent;
    color: $color-black;
    border: 1px solid $color-black;
  }

  &--outline-light {
    background-color: transparent;
    color: $color-white;
    border: 1px solid $color-white;
  }

  &--underline {
    background-color: transparent;
    color: $color-white;
    border: none;
    border-radius: 0;
    padding-left: 0;
    padding-right: 0;
    text-decoration: underline;
    text-underline-offset: 8px;
  }

  &--disabled {
    opacity: 0.3;
  }

  &--with-icon .slot {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;

    .icon {
      font-size: 20px;
      line-height: 100%;
    }
  }

  &--icon-only {
    padding: 14px;
    border-radius: 50%;

    &.button--small {
      padding: 10px;
    }
  }

  &--full {
    display: block;
    width: 100%;
  }

  &--loading {
    position: relative;

    .slot {
      opacity: 0;
    }

    .loading {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      font-size: 20px;
    }

    .icon.icon-spinner {
      transform-origin: center;
      display: inline-block;
      animation: spinner 0.75s infinite linear;
    }

    @keyframes spinner {
      100% {
        transform: rotate(360deg);
      }
    }
  }
}
</style>
